/*!
 * windowUtils module provides standalone functions for accessing browser window APIs.
 *
 * This improves:
 * - compatibility with various environments where the `window` global variable may not be available (VegaOS)
 * - testability by allowing easy mocking of window-dependent functionality.
 */

import {isNumber} from './numberUtils';
import {isValidString} from './stringUtils';
import {isFunction, isNullish} from './Utils';

/**
 * Get `window.devicePixelRatio` (defaults to `1` if not available)
 */
export const getWindowDevicePixelRatio = (): number => {
  if (isNullish(window) || isNullish(window.devicePixelRatio)) {
    return 1;
  }
  return window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
};

/**
 * Get `window.screen` dimensions with device pixel ratio applied
 */
export const getWindowScreenDimensions = (): {height: number; scale: number; width: number} | undefined => {
  if (isNullish(window) || isNullish(window.screen)) {
    return undefined;
  }

  if (!isNumber(window.screen.width) || !isNumber(window.screen.height)) {
    return undefined;
  }

  const scale = getWindowDevicePixelRatio();
  const width = window.screen.width * scale;
  const height = window.screen.height * scale;

  if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) {
    return undefined;
  }

  return {width, height, scale};
};

/**
 * Add event listener to window
 *
 * Use this for window-level events that relate to the browser viewport/tab:
 * - resize: viewport size changes
 * - scroll: page scroll (when listening at window level)
 * - load/unload/beforeunload: page lifecycle
 * - focus/blur: window/tab focus state
 * - hashchange/popstate: navigation events
 * - storage: localStorage changes from other tabs
 *
 * Note: Events bubble up from target → document → window, so window receives
 * many events that also fire on document, but not vice versa.
 */
export const addWindowEventListener = (event: string, handler: EventListenerOrEventListenerObject): void => {
  if (isNullish(window) || !isFunction(window.addEventListener) || isNullish(handler)) {
    return;
  }
  // any-wrap needed to not confirm to type checks
  // we use vendor-prefixed events that are not part of the interface
  (window.addEventListener as any)(event, handler);
};

/**
 * Remove event listener from window
 *
 * @see addWindowEventListener for details on when to use window vs document event listeners
 */
export const removeWindowEventListener = (event: string, handler: EventListenerOrEventListenerObject): void => {
  if (isNullish(window) || !isFunction(window.removeEventListener) || isNullish(handler)) {
    return;
  }

  // any-wrap needed to not confirm to type checks
  // we use vendor-prefixed events that are not part of the interface
  (window.removeEventListener as any)(event, handler);
};

/**
 * Get `window.location.hostname`
 */
export const getWindowLocationHostname = (): string | undefined => {
  if (isNullish(window) || isNullish(window.location)) {
    return undefined;
  }

  if (!isValidString(window.location.hostname)) {
    return undefined;
  }

  return window.location.hostname;
};

/**
 * Get `window.location.pathname`
 */
export const getWindowLocationPathname = (): string | undefined => {
  if (isNullish(window) || isNullish(window.location)) {
    return undefined;
  }

  if (!isValidString(window.location.pathname)) {
    return undefined;
  }

  return window.location.pathname;
};

/**
 * Get `window.performance`
 */
export const getWindowPerformance = (): Performance | undefined => {
  if (isNullish(window)) {
    return undefined;
  }

  return window.performance;
};

/**
 * Get from `window` the WebOS-specific API objects (for WebOS TV support)
 */
export const getWindowWebOSApi = (): {PalmSystem?: any; webOSSystem?: any} => {
  if (isNullish(window)) {
    return {};
  }

  return {
    webOSSystem: window.webOSSystem,
    PalmSystem: window.PalmSystem,
  };
};

/**
 * Check if WebOS API is available (for WebOS TV support)
 */
export const hasWindowWebOSApi = (): boolean => {
  const {webOSSystem, PalmSystem} = getWindowWebOSApi();
  return !isNullish(webOSSystem) || !isNullish(PalmSystem);
};

/**
 * Get `window.navigator.language` (with fallback to `window.navigator.userLanguage` for IE)
 */
export const getWindowNavigatorLanguage = (): string | undefined => {
  if (isNullish(window) || isNullish(window.navigator)) {
    return undefined;
  }

  return window.navigator.language || (window.navigator as any).userLanguage;
};

/**
 * Get `window.navigator.userAgent`
 */
export const getWindowNavigatorUserAgent = (): string | undefined => {
  if (isNullish(window?.navigator) || !isValidString(window.navigator.userAgent)) {
    return undefined;
  }

  return window.navigator.userAgent;
};

/**
 * Get `window.videojs.VERSION`
 */
export const getWindowVideojsVersion = (): string | undefined => {
  if (isNullish(window)) {
    return undefined;
  }

  return window['videojs']?.['VERSION'];
};

/**
 * Get `window.THEOplayer.VERSION`
 */
export const getWindowTheoPlayerVersion = (): string | undefined => {
  if (isNullish(window)) {
    return undefined;
  }

  return window['THEOplayer']?.version;
};
